import type { Ref } from 'react';
import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import clsx from 'clsx';
import { detectIfTruncated, isObject } from '../../common-functions';
import Tooltip from '../../tooltip-component/tooltip.component';

const renderDataCellValue = (value: unknown): bigint | number | string | undefined => {
  const type = typeof value;
  if (type === 'bigint' || type === 'number' || type === 'string') {
    return value as bigint | number | string;
  }

  if (typeof value === 'boolean') {
    const newValue = value.toString();
    return newValue.charAt(0).toUpperCase() + newValue.substring(1);
  }

  if (value instanceof Array) {
    return value.map(renderDataCellValue).join(', ');
  }

  if (isObject(value)) {
    if ('text' in value) {
      return value.text as string;
    }

    if ('value' in value) {
      return value.value as string | number;
    }

    if ('caption' in value) {
      return value.caption as string;
    }

    if ('name' in value) {
      return value.name as string;
    }

    const keys = Object.keys(value);
    let objString = '{';
    for (let j = 0, oLength = keys.length; j < oLength; j++) {
      objString += ` ${keys[j]}: ${renderDataCellValue(value[keys[j]])},`;
    }
    objString += ' }';

    return objString;
  }
};

export interface DataGridDataCellProps {
  value: unknown;
  width?: number;
  allowCellScrollBar?: boolean;
  renderAsHTML?: boolean;
  renderAsInnerHTML?: boolean;
}

function DataGridDataCell(
  { value, width, allowCellScrollBar, renderAsHTML = false, renderAsInnerHTML = false }: DataGridDataCellProps,
  forwardRef: Ref<HTMLDivElement>
) {
  const cellRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useImperativeHandle(forwardRef, () => cellRef.current as HTMLDivElement);

  useEffect(() => {
    if (cellRef.current) {
      setIsTruncated(detectIfTruncated(cellRef.current));
    }
  }, [width]);

  // NOTE: if modifying the padding 'odin-pl-2', please remember to update all other cells as well as 'CELL_PADDING_LEFT_PX'
  const cellClasses = clsx('odin-pl-2', {
    'odin-truncate': !allowCellScrollBar,
    'odin-max-h-20 odin-overflow-y-auto odin-leading-none custom-scroll scrollbar-colored odin-p-1.5 odin-overflow-x-hidden odin-break-words odin-whitespace-pre-line':
      allowCellScrollBar,
  });

  if (renderAsHTML && React.isValidElement(value)) {
    return (
      <div className={cellClasses} ref={cellRef}>
        {value}
      </div>
    );
  } else if (renderAsInnerHTML && value) {
    return <div className={cellClasses} ref={cellRef} dangerouslySetInnerHTML={{ __html: String(value) }} />;
  }

  const cellValue = renderDataCellValue(value);

  return (
    <Tooltip
      description={String(cellValue)}
      tooltipClassName="odin-bg-mono-1"
      disabled={!isTruncated || allowCellScrollBar}
      tooltipZIndexCheck={true}
    >
      <div className={cellClasses} ref={cellRef}>
        {String(cellValue)}
      </div>
    </Tooltip>
  );
}

export default forwardRef(DataGridDataCell);
